/* ============================================
   Premium Button Component - Theme Support
   Light & Dark mode compatible
   ============================================ */

.premium-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.5;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  border: none;
  outline: none;
  user-select: none;
  box-shadow: none;
}

/* Button Content */
.premium-button__content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  transition: transform 0.2s ease;
}

/* Button Variants */
.premium-button--primary {
  background: linear-gradient(135deg, var(--accent-primary, #C9A227) 0%, var(--accent-secondary, #E0C98E) 100%);
  color: #0A1628;
  box-shadow: 0 2px 8px rgba(201, 162, 39, 0.25);
}

.premium-button--primary:hover {
  transform: translateY(-2px);
  box-shadow:
    0 4px 16px rgba(201, 162, 39, 0.4),
    0 0 0 1px rgba(201, 162, 39, 0.1);
  background: linear-gradient(135deg, #E0C98E 0%, #D4B560 100%);
}

.premium-button--primary:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(201, 162, 39, 0.3);
}

.premium-button--secondary {
  background: var(--bg-card);
  color: var(--accent-primary);
  border: 2px solid var(--accent-primary);
}

.theme-dark .premium-button--secondary {
  background: rgba(255, 255, 255, 0.05);
}

.premium-button--secondary:hover {
  background: var(--accent-bg);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(201, 162, 39, 0.15);
}

.theme-dark .premium-button--secondary:hover {
  background: rgba(201, 162, 39, 0.15);
}

.premium-button--tertiary {
  background: transparent;
  color: var(--accent-primary);
}

.premium-button--tertiary:hover {
  background: var(--accent-bg);
}

.theme-dark .premium-button--tertiary:hover {
  background: rgba(201, 162, 39, 0.1);
}

.premium-button--danger {
  background-color: #dc2626;
  color: white;
}

.premium-button--danger:hover {
  background-color: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.premium-button--success {
  background: linear-gradient(135deg, #C9A227 0%, #E0C98E 100%);
  color: #ffffff;
}

.premium-button--success:hover {
  background: linear-gradient(135deg, #E0C98E 0%, #D4B560 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(201, 162, 39, 0.4);
}

.premium-button--outline {
  background: transparent;
  border: 2px solid var(--border-primary, rgba(255, 255, 255, 0.2));
  color: var(--text-secondary, #e2e8f0);
}

.theme-dark .premium-button--outline,
.webinar-list-container:not(.theme-light) .premium-button--outline,
.filter-controls .premium-button--outline {
  border-color: rgba(255, 255, 255, 0.3);
  color: #e2e8f0;
  background: rgba(255, 255, 255, 0.05);
}

/* Light theme outline button */
.theme-light .premium-button--outline,
.webinar-list-container.theme-light .premium-button--outline {
  background: #ffffff;
  border: 2px solid #C9A227;
  color: #E0C98E;
}

.premium-button--outline:hover {
  border-color: var(--accent-primary, #C9A227);
  color: var(--accent-primary, #C9A227);
  background: var(--bg-secondary, rgba(201, 162, 39, 0.1));
}

.theme-dark .premium-button--outline:hover,
.webinar-list-container:not(.theme-light) .premium-button--outline:hover,
.filter-controls .premium-button--outline:hover {
  background: rgba(201, 162, 39, 0.15);
  border-color: #C9A227;
  color: #66E5FF;
}

/* Light theme outline button hover */
.theme-light .premium-button--outline:hover,
.webinar-list-container.theme-light .premium-button--outline:hover {
  background: rgba(201, 162, 39, 0.1);
  border-color: #E0C98E;
  color: #E0C98E;
}

.premium-button--text {
  background: transparent;
  color: var(--accent-primary);
  box-shadow: none;
}

.premium-button--text:hover {
  background: var(--accent-bg);
  box-shadow: none;
}

.theme-dark .premium-button--text:hover {
  background: rgba(201, 162, 39, 0.1);
}

/* Ghost variant */
.premium-button--ghost {
  background: transparent;
  color: var(--text-secondary);
}

.theme-dark .premium-button--ghost {
  color: rgba(255, 255, 255, 0.7);
}

.premium-button--ghost:hover {
  background: var(--bg-tertiary);
}

.theme-dark .premium-button--ghost:hover {
  background: rgba(255, 255, 255, 0.05);
}

/* Button Sizes */
.premium-button--small {
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  border-radius: 6px;
}

.premium-button--medium {
  padding: 0.75rem 1.5rem;
  font-size: 0.95rem;
}

.premium-button--large {
  padding: 1rem 2rem;
  font-size: 1rem;
  border-radius: 10px;
}

/* Full Width Button */
.premium-button--full-width {
  width: 100%;
  display: flex;
}

/* Disabled State */
.premium-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.premium-button--disabled:hover {
  transform: none;
  box-shadow: none;
}

/* Loading State */
.premium-button--loading {
  cursor: wait;
}

.premium-button--loading .premium-button__content {
  opacity: 0.5;
}

/* Spinner Animation */
.premium-button__spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
}

.premium-button__spinner-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: currentColor;
  margin: 0 2px;
  animation: pulse 1.2s infinite ease-in-out;
}

.premium-button__spinner-dot:nth-child(1) {
  animation-delay: 0s;
}

.premium-button__spinner-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.premium-button__spinner-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.6;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Ripple Effect */
.premium-button__ripple {
  position: absolute;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  transform: scale(0);
  animation: ripple 0.6s linear;
  pointer-events: none;
  width: 150px;
  height: 150px;
  margin-left: -75px;
  margin-top: -75px;
}

@keyframes ripple {
  to {
    transform: scale(2);
    opacity: 0;
  }
}

/* Success State Animation */
.premium-button--success-animation {
  animation: successPulse 0.5s ease-in-out;
}

@keyframes successPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(201, 162, 39, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(201, 162, 39, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(201, 162, 39, 0);
  }
}

/* Error State Animation */
.premium-button--error {
  animation: errorShake 0.5s ease-in-out;
}

@keyframes errorShake {
  0%, 100% {
    transform: translateX(0);
  }
  20%, 60% {
    transform: translateX(-5px);
  }
  40%, 80% {
    transform: translateX(5px);
  }
}

/* Icon Positioning */
.premium-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.premium-button__icon--left {
  margin-right: 0.5rem;
}

.premium-button__icon--right {
  margin-left: 0.5rem;
}

/* Focus States - Accessibility */
.premium-button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(201, 162, 39, 0.4);
}

.premium-button--primary:focus-visible {
  box-shadow: 0 0 0 3px rgba(201, 162, 39, 0.4), 0 4px 12px rgba(201, 162, 39, 0.4);
}

.premium-button--danger:focus-visible {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.4);
}

.premium-button--success:focus-visible {
  box-shadow: 0 0 0 3px rgba(201, 162, 39, 0.4);
}

/* Active press state */
.premium-button:active:not(:disabled) {
  transform: scale(0.98) translateY(1px);
}

/* Accessibility - Respect reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .premium-button {
    transition: none;
  }

  .premium-button:hover {
    transform: none;
  }

  .premium-button__ripple {
    animation: none;
    opacity: 0.3;
    transform: scale(1);
  }

  .premium-button--success-animation,
  .premium-button--error {
    animation: none;
  }

  .premium-button__spinner-dot {
    animation: none;
    opacity: 1;
  }
}
